import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

/**
 * Modal - Reusable modal/dialog component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal should close
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.maxWidth - Max width class (default: 'max-w-md')
 * @param {boolean} props.showCloseButton - Whether to show close button (default: true)
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth = 'max-w-md',
  showCloseButton = true
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <Card 
        className={`w-full ${maxWidth} bg-blue-900 border border-blue-700`}
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">{title}</CardTitle>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Close modal"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {children}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * ModalActions - Standard action buttons for modals
 * 
 * @param {Object} props
 * @param {Function} props.onConfirm - Confirm button handler
 * @param {Function} props.onCancel - Cancel button handler
 * @param {string} props.confirmText - Confirm button text (default: 'Confirm')
 * @param {string} props.cancelText - Cancel button text (default: 'Cancel')
 * @param {boolean} props.confirmDisabled - Whether confirm button is disabled
 * @param {boolean} props.loading - Whether to show loading state
 * @param {string} props.variant - Button variant ('default' | 'destructive')
 */
export const ModalActions = ({ 
  onConfirm, 
  onCancel, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel',
  confirmDisabled = false,
  loading = false,
  variant = 'default'
}) => {
  const confirmClasses = variant === 'destructive'
    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold'
    : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-blue-900 font-semibold';

  return (
    <div className="flex space-x-2 mt-4">
      <Button 
        onClick={onConfirm}
        disabled={confirmDisabled || loading}
        className={`flex-1 ${confirmClasses}`}
      >
        {loading ? 'Processing...' : confirmText}
      </Button>
      <Button 
        onClick={onCancel}
        variant="outline" 
        disabled={loading}
        className="flex-1 border-sky-400 text-sky-300 hover:bg-blue-800 hover:text-white"
      >
        {cancelText}
      </Button>
    </div>
  );
};

export default Modal;
